import { dateRangeSchema, levelSelectSchema } from '@retail/calculus/components';
import { CalculusTFunction } from '@retail/calculus/i18n';
import { freightType, FreightType } from '@retail/freight/types';
import * as yup from 'yup';

const minPercent = -100;
const maxPercent = 100;

export const freightConditionsSchema = (t: CalculusTFunction) => {
  const numberRangeError = t('calculus.error.mustBeNumberBetween', {
    min: minPercent,
    max: maxPercent,
  });
  return yup.object({
    freightType: yup
      .mixed<FreightType>()
      .oneOf(Object.values(freightType), t('calculus.error.requiredField'))
      .required(t('calculus.error.requiredField'))
      .default(''),
    percentAddon: yup
      .number()
      .typeError(numberRangeError)
      .nullable()
      .default(null)
      .when('freightType', {
        is: (freightType: string) => freightType === null || freightType === 'percent',
        then: yup
          .number()
          .typeError(numberRangeError)
          .transform((_, value: string | null) => {
            if (value) {
              return Number((value + '').replace(',', '.'));
            }
            return null;
          })
          .min(minPercent, numberRangeError)
          .max(maxPercent, numberRangeError)
          .required(t('calculus.error.requiredField')),
      }),
    fixedAddon: yup
      .number()
      .default(null)
      .nullable()
      .when('freightType', {
        is: 'fixed_addon',
        then: yup
          .number()
          .typeError(t('calculus.error.mustBeNumber'))
          .transform((_, value: string | null) => {
            if (value) {
              return Number((value + '').replace(',', '.'));
            }
            return null;
          })
          .positive(t('calculus.error.mustBeNumber'))
          .required(t('calculus.error.requiredField')),
      }),
    fixedAddonUnit: yup
      .string()
      .default('')
      .nullable()
      .when('freightType', {
        is: 'fixed_addon',
        then: yup
          .string()
          .typeError(t('calculus.error.unitIsRequired'))
          .required(t('calculus.error.requiredField')),
      }),
  });
};

export const createFreightConditionSchema = (t: CalculusTFunction) =>
  levelSelectSchema(t).concat(dateRangeSchema(t)).concat(freightConditionsSchema(t));

export const editFreightConditionSchema = (t: CalculusTFunction) =>
  dateRangeSchema(t).concat(freightConditionsSchema(t));

export type CreateFreightConditionSchema = yup.InferType<
  ReturnType<typeof createFreightConditionSchema>
>;
export type EditFreightConditionSchema = yup.InferType<
  ReturnType<typeof editFreightConditionSchema>
>;
